import AuthHeader from "./AuthHeader";
import { API_URLS } from "../Constants";
import { debugHelper } from "../utils/debug";

const POLL_INTERVAL_MS = Number(process.env.REACT_APP_PDF_POLL_INTERVAL_MS) || 2000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const toError = async (response, fallback) => {
    let message = fallback;
    try {
        const data = await response.json();
        message = data?.message || data?.error || (typeof data === "string" ? data : fallback);
    } catch (e) {
        // not JSON - keep fallback message
    }
    const error = new Error(message);
    error.status = response.status;
    return error;
};

//Resolves relative urls returned by the server (statusUrl/downloadUrl) against the API base.
const resolveUrl = (url, fallback) => url || fallback;

//Converts a base64 string into a Blob of the given type.
const base64ToBlob = (base64, type) => {
    const binaryString = atob(base64);
    const binaryData = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        binaryData[i] = binaryString.charCodeAt(i);
    }
    return new Blob([binaryData], { type });
};

//Triggers a browser download for the given blob.
export const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Downloads mitigation plan PDFs for the given roadway segments.
 *
 * - One segment: the server responds 200 with a base64 PDF which is saved directly.
 * - Several segments: the server responds 202 with a job id. The job status is polled
 *   every couple of seconds (reporting progress through `onProgress`) and, once done,
 *   the zip archive is fetched as a blob and saved.
 *
 * @param {Array<string|number>} segmentIds - 1 to 100 segment ids
 * @param {Object} [options]
 * @param {function({completed:number,total:number,status:string}):void} [options.onProgress]
 * @param {AbortSignal} [options.signal] - aborts polling/downloading when triggered
 * @returns {Promise<{filename:string}>}
 */
export const downloadMitigationPlans = async (segmentIds, { onProgress, signal } = {}) => {
    const headers = { "Content-type": "application/json; charset=UTF-8", ...AuthHeader() };

    const startResponse = await fetch(API_URLS["API_DOWNLOAD_PDF"], {
        method: "POST",
        headers,
        body: JSON.stringify({ segmentIds }),
        signal,
    });

    //Single segment: PDF is returned inline.
    if (startResponse.status === 200) {
        const data = await startResponse.json();
        if (!data?.success || !data.data) {
            throw new Error(data?.message || "The server did not return a PDF.");
        }
        const filename = data.filename || `mitigation-plan-roadway-segment-${segmentIds[0]}.pdf`;
        saveBlob(base64ToBlob(data.data, "application/pdf"), filename);
        return { filename };
    }

    if (startResponse.status !== 202) {
        throw await toError(startResponse, "Unable to start the PDF download.");
    }

    //Several segments: job queued in the background.
    const job = await startResponse.json();
    debugHelper.log("PDF job queued:", job);
    const jobId = job.jobId;
    const statusUrl = resolveUrl(job.statusUrl, `${API_URLS["API_DOWNLOAD_PDF"]}/jobs/${jobId}`);
    let downloadUrl = resolveUrl(job.downloadUrl, `${API_URLS["API_DOWNLOAD_PDF"]}/jobs/${jobId}/download`);

    onProgress?.({ completed: 0, total: job.total ?? segmentIds.length, status: job.status || "queued" });

    //Poll until the job is done or failed.
    let status;
    for (; ;) {
        await sleep(POLL_INTERVAL_MS);
        if (signal?.aborted) throw new DOMException("Download cancelled.", "AbortError");

        const statusResponse = await fetch(statusUrl, { headers: AuthHeader(), signal });
        if (statusResponse.status === 404) {
            throw await toError(statusResponse, "The download job has expired. Please try again.");
        }
        if (!statusResponse.ok) {
            throw await toError(statusResponse, "Unable to check the download progress.");
        }

        status = await statusResponse.json();
        debugHelper.log("PDF job status:", status);
        onProgress?.({
            completed: status.completed ?? 0,
            total: status.total ?? segmentIds.length,
            status: status.status,
        });

        if (status.status === "done") {
            downloadUrl = resolveUrl(status.downloadUrl, downloadUrl);
            break;
        }
        if (status.status === "failed") {
            const failed = status.failedSegmentIds?.length
                ? ` Failed segment(s): ${status.failedSegmentIds.join(", ")}.`
                : "";
            throw new Error((status.message || "PDF generation failed.") + failed);
        }
    }

    //Fetch the zip archive.
    const downloadResponse = await fetch(downloadUrl, { headers: AuthHeader(), signal });
    if (downloadResponse.status === 410) {
        throw await toError(downloadResponse, "The generated files are no longer available. Please try again.");
    }
    if (!downloadResponse.ok) {
        throw await toError(downloadResponse, "Unable to download the generated files.");
    }

    const disposition = downloadResponse.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : "mitigation-plans.zip";

    const blob = await downloadResponse.blob();
    saveBlob(blob, filename);
    return { filename };
};
